import { useState, useRef, useCallback } from "react";
/** Forecast-animation domain: animPhase/branchAnim state, per-run interval refs, starters. */
export function useForecastAnimation() {
  const [animPhase, setAnimPhase] = useState(null);
  const [branchAnim, setBranchAnim] = useState(null); // {branchId, revealCount, lineProgress, totalEvents}
  const animTimerRef = useRef(null);
  const branchAnimRef = useRef(null);
  const branchClearRef = useRef(null);

  // Animation: 3 seconds
  const startForecastAnim = useCallback((eventCount, opts = {}) => {
    if (animTimerRef.current) clearInterval(animTimerRef.current);
    const total = eventCount + 1;
    const perEvent = 2400 / total;
    let count = 0;
    setAnimPhase({
      revealCount: 0,
      lineProgress: 0,
      totalEvents: total,
      skipPastSweep: !!opts.skipPastSweep,
    });
    animTimerRef.current = setInterval(() => {
      count++;
      if (count <= total) {
        setAnimPhase((p) => (p ? { ...p, revealCount: count } : null));
      } else {
        const lineT = Math.min(1, ((count - total) * perEvent) / 600);
        setAnimPhase((p) => (p ? { ...p, lineProgress: lineT } : null));
        if (lineT >= 1) {
          clearInterval(animTimerRef.current);
          animTimerRef.current = null;
          setTimeout(() => setAnimPhase(null), 200);
        }
      }
    }, perEvent);
  }, []);

  // Branch animation: 3 seconds. ForecastLane withholds the branch result until
  // `revealCount >= totalEvents`; `opts.keepResult` keeps an already-shown result on screen
  // while triggers that arrived after it sweep in.
  const startBranchAnim = useCallback((branchId, eventCount, opts = {}) => {
    if (branchAnimRef.current) clearInterval(branchAnimRef.current);
    // A finished sweep's pending clear would otherwise cut this one short.
    if (branchClearRef.current) clearTimeout(branchClearRef.current);
    branchClearRef.current = null;
    const total = eventCount + 1;
    const perEvent = 2400 / total;
    let count = 0;
    setBranchAnim({
      branchId,
      revealCount: 0,
      lineProgress: 0,
      totalEvents: total,
      ...(opts.keepResult ? { keepResult: true } : {}),
    });
    branchAnimRef.current = setInterval(() => {
      count++;
      if (count <= total) {
        setBranchAnim((p) => (p ? { ...p, revealCount: count } : null));
      } else {
        const lineT = Math.min(1, ((count - total) * perEvent) / 600);
        setBranchAnim((p) => (p ? { ...p, lineProgress: lineT } : null));
        if (lineT >= 1) {
          clearInterval(branchAnimRef.current);
          branchAnimRef.current = null;
          branchClearRef.current = setTimeout(() => {
            branchClearRef.current = null;
            setBranchAnim(null);
          }, 200);
        }
      }
    }, perEvent);
  }, []);

  return {
    animPhase,
    setAnimPhase,
    branchAnim,
    setBranchAnim,
    startForecastAnim,
    startBranchAnim,
    // animTimerRef/branchAnimRef are internal to the starters; not returned.
  };
}
